'use client';

import MagicJump, { JumpTarget } from '@/components/MagicJump';
import { EPlatform } from '@/utils/enums/platform-enum';
import Image from '@/components/Image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

// Banner元素接口
interface BannerElement {
  span: number; // 栅格占比（类似24栅格系统）
  image: string; // 图片URL
  imageAlt?: string; // 图片alt
  linkTo: JumpTarget; // 跳转配置
}

// Banner行类型（一行可能包含多个元素）
type BannerRow = BannerElement[];

interface ActivePageListBannerProps {
  list: BannerRow[]; // 二维数组：list[row][col]
  deviceType: EPlatform;
  pageName?: string; // 移动端面包屑标题
  onExp?: (params: any) => void;
  onClick?: (params: any) => void;
}

export default function ActivePageListBanner({
  list,
  deviceType,
  pageName,
  onExp,
  onClick,
}: ActivePageListBannerProps) {
  const [exposedBanners, setExposedBanners] = useState<Set<string>>(new Set());
  const bannerRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const isWap = deviceType !== EPlatform.WEB;

  // 曝光监听
  useEffect(() => {
    if (!onExp) return;

    const observers: IntersectionObserver[] = [];

    bannerRefs.current.forEach((ref, key) => {
      if (!ref || exposedBanners.has(key)) return;

      const [rowIndex, colIndex] = key.split('-').map(Number);

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !exposedBanners.has(key)) {
              setExposedBanners((prev) => new Set(prev).add(key));

              const element = list[rowIndex][colIndex];
              onExp({
                module_name: `banner${rowIndex + 1}`,
                mkt_pits_index: colIndex,
                mkt_pits_jump_type: `${element.linkTo.jump_type}`,
                mkt_pits_jump_content: element.linkTo.Jump_target,
              });
            }
          });
        },
        {
          threshold: 0.5,
        },
      );

      observer.observe(ref);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [onExp, list, exposedBanners]);

  // 处理Banner点击
  const handleBannerClick = (rowIndex: number, colIndex: number) => {
    const element = list[rowIndex][colIndex];
    onClick?.({
      module_name: `banner${rowIndex + 1}`,
      mkt_pits_index: colIndex,
      mkt_pits_jump_type: `${element.linkTo.jump_type}`,
      mkt_pits_jump_content: element.linkTo.Jump_target,
    });
  };

  // 计算栅格宽度百分比（基于24栅格系统）
  const getColWidth = (span: number) => {
    return `${(span / 24) * 100}%`;
  };

  if (!list || list.length === 0) {
    return null;
  }

  return (
    <div className="w-full mb-[16px] lg:mb-[20px] relative">
      {/* 移动端面包屑导航 - 绝对定位在banner上 */}
      {isWap && pageName && (
        <div className="absolute top-0 left-0 z-10 py-[11px] px-[13px] pb-[8px] bg-gradient-to-b from-black/100 to-black/0">
          <div className="flex items-center gap-1 text-white text-[12px]">
            <Link href="/" className="hover:underline">
              Home
            </Link>
            <span>&nbsp;/&nbsp;</span>
            <span>{pageName}</span>
          </div>
        </div>
      )}

      {/* Banner列表 */}
      <div className="w-full">
        {list.map((row, rowIndex) => (
          <div key={`banner-row-${rowIndex}`} className="flex w-full mb-0 last:mb-0">
            {row.map((element, colIndex) => {
              const key = `${rowIndex}-${colIndex}`;

              return (
                <div
                  key={`banner-${rowIndex}-${colIndex}`}
                  style={{ width: getColWidth(element.span) }}
                  className="flex-shrink-0"
                >
                  <MagicJump
                    item={element.linkTo}
                    onBeforeJump={() => handleBannerClick(rowIndex, colIndex)}
                  >
                    <div
                      ref={(el) => {
                        if (el) {
                          bannerRefs.current.set(key, el);
                        }
                      }}
                      className="w-full h-auto cursor-pointer"
                    >
                      <Image
                        src={element.image}
                        alt={element.imageAlt || `Banner ${rowIndex + 1}-${colIndex + 1}`}
                        width={1200}
                        height={600}
                        className="w-full h-auto block"
                        sizes="(max-width: 1024px) 100vw, 1440px"
                        priority={rowIndex === 0}
                      />
                    </div>
                  </MagicJump>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
